import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { pTicket } from '../../../interfaces/pTicket';
import { PTicketService } from '../../../services/p-ticket.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { compareJSONValues } from '../../../helpers/genericFunctions';
import { openSnackBar } from '../../../helpers/genericFunctions';
import {
  MatAutocompleteModule,
  MatAutocompleteSelectedEvent,
} from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { PhoneNumberComponent } from '../../generic/phone-number/phone-number.component';
import { Product } from '../../../interfaces/pProduct';
import { ProductService } from '../../../services/product.service';
import { MasterDataService } from '../../../services/master-data.service';
import { debounceTime, filter, switchMap, tap } from 'rxjs';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'app-p-ticket-edit-user-data',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    CommonModule,
    MatAutocompleteModule,
    MatChipsModule,
    PhoneNumberComponent,
  ],
  templateUrl: './p-ticket-edit-user-data.component.html',
  styleUrl: './p-ticket-edit-user-data.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PTicketEditUserDataComponent {
  constructor(
    private formBuilder: FormBuilder,
    private masterDataService: MasterDataService,
    private productService: ProductService,
    private pTicketService: PTicketService,
    private snackBar: MatSnackBar,
  ) {}
  selectable = true;
  removable = true;
  addOnBlur = true;
  @Input() canUpdate: boolean = false;
  @Input() pTicket!: pTicket;
  @Output() pDescriptionAddedEmitter = new EventEmitter();
  products!: Product[];

  editpTicketForm = this.formBuilder.group({
    pct_useremail: new FormControl('', Validators.required),
    pct_user: new FormControl('', Validators.required),
    pct_company: new FormControl('', Validators.required),
    pct_product: new FormControl('', Validators.required),
    pct_initialdescription: new FormControl('', Validators.required),
    pct_callbacknumber: new FormControl('', Validators.required),
    pct_responsename: new FormControl('', Validators.required),
    pct_responseemail: new FormControl('', Validators.required),
    pct_countrycode: new FormControl('', Validators.required),
    pct_supportlevel: new FormControl('', Validators.required),
    pct_tickettype: new FormControl('', Validators.required),
  });

  companyRecommendations: pTicket[] = [];
  isLoadingCompanyRecommendations: boolean = false;
  onCompanySelected(event: MatAutocompleteSelectedEvent): void {
    this.editpTicketForm.patchValue({
      pct_company: event.option.value.pct_company,
      pct_countrycode: event.option.value.pct_countrycode,
    });
  }

  ngOnInit() {
    this.masterDataService
      .getMasterCountrycodeMockList()
      .subscribe((countryCodes) => {
        this.fillForm();
        if (this.pTicket.pct_status === 'closed') {
          this.pTicketClose();
        } else {
          this.pTicketOpen();
        }
      });
    this.productService
      .getProducts()
      .subscribe((products) => (this.products = products));
    this.editpTicketForm
      .get('pct_company')!
      .valueChanges.pipe(
        debounceTime(300),
        filter((val) => val !== null && val !== ''),
        tap(() => (this.isLoadingCompanyRecommendations = true)),
        switchMap((val) => {
          const start = new Date(2023, 0, 1);
          start.setHours(0, 0, 0, 0);
          const end = new Date();
          end.setHours(23, 59, 59, 59);
          const httpParams = new HttpParams()
            .append('updatedAt[$gte]', start.toISOString())
            .append('updatedAt[$lte]', end.toISOString())
            .append('pct_company', val || '');
          return this.pTicketService.getAll(httpParams);
        }),
      )
      .subscribe(({ data: pTicketList }) => {
        this.companyRecommendations = Array.from(
          pTicketList
            .reduce((map, ticket) => {
              const key = [
                ticket.pct_company?.trim(),
                ticket.pct_countrycode?.trim(),
              ].join('|');
              if (!map.has(key)) map.set(key, ticket);
              return map;
            }, new Map<string, pTicket>())
            .values(),
        ).sort((a, b) => a.pct_company.localeCompare(b.pct_company));

        this.isLoadingCompanyRecommendations = false;
      });
  }

  ngOnChanges(): void {
    console.log('changes');
    if (!this.pTicket) return;

    if (!this.canUpdate) {
      this.editpTicketForm.enable();
    } else {
      this.editpTicketForm.disable();
    }
  }
  ngAfterViewInit(): void {
    if (!this.canUpdate) {
      this.editpTicketForm.enable();
    } else {
      this.editpTicketForm.disable();
    }
  }

  fillForm(): void {
    this.editpTicketForm.patchValue({
      pct_useremail: this.pTicket.pct_useremail,
      pct_user: this.pTicket.pct_user,
      pct_company: this.pTicket.pct_company,
      pct_product: this.pTicket.pct_product,
      pct_initialdescription: this.pTicket.pct_initialdescription,
      pct_callbacknumber: this.pTicket.pct_callbacknumber,
      pct_responsename: this.pTicket.pct_responsename,
      pct_responseemail: this.pTicket.pct_responseemail,
      pct_countrycode: this.pTicket.pct_countrycode,
      pct_supportlevel: this.pTicket.pct_supportlevel,
      pct_tickettype: this.pTicket.pct_tickettype,
    });
  }
  /**
   * gets called on if this form needs to change pTicket data
   */
  onUserDataUpdate(): void {
    // setup messaging
    let success_message = 'User data successfully updated ';
    let error_message = 'Error while updating user data ';
    // find differences that should be logged into the ticket
    let differences = [];
    differences = compareJSONValues(this.pTicket, this.editpTicketForm.value);
    if (differences.length == 0) {
      openSnackBar('Nothing to change', 2000, this.snackBar);
      this.pDescriptionAddedEmitter.emit();
    } else {
      // create the logging description and update the ticket
      this.pTicketService
        .updatePTicket(
          this.pTicket.id,
          this.editpTicketForm.value,
          differences,
          success_message,
          error_message,
        )
        .subscribe(() => {
          this.pTicketService
            .getTicket(this.pTicket.id)
            .subscribe((pTicket) => {
              this.pTicket = pTicket;
              this.pDescriptionAddedEmitter.emit();
            });
        });
    }
  }
  pTicketOpen() {
    this.editpTicketForm.get('pct_useremail')?.enable();
    this.editpTicketForm.get('pct_user')?.enable();
    this.editpTicketForm.get('pct_company')?.enable();
    this.editpTicketForm.get('pct_product')?.enable();
    this.editpTicketForm.get('pct_initialdescription')?.enable();
    this.editpTicketForm.get('pct_callbacknumber')?.enable();
    this.editpTicketForm.get('pct_responsename')?.enable();
    this.editpTicketForm.get('pct_responseemail')?.enable();
    this.editpTicketForm.get('pct_countrycode')?.enable();
    this.editpTicketForm.get('pct_supportlevel')?.enable();
    this.editpTicketForm.get('pct_tickettype')?.enable();
  }
  pTicketClose() {
    this.editpTicketForm.get('pct_useremail')?.disable();
    this.editpTicketForm.get('pct_user')?.disable();
    this.editpTicketForm.get('pct_company')?.disable();
    this.editpTicketForm.get('pct_product')?.disable();
    this.editpTicketForm.get('pct_initialdescription')?.disable();
    this.editpTicketForm.get('pct_callbacknumber')?.disable();
    this.editpTicketForm.get('pct_responsename')?.disable();
    this.editpTicketForm.get('pct_responseemail')?.disable();
    this.editpTicketForm.get('pct_countrycode')?.disable();
    this.editpTicketForm.get('pct_supportlevel')?.disable();
    this.editpTicketForm.get('pct_tickettype')?.disable();
  }
}
